import type {
  DOMNode,
  Element,
  HTMLReactParserOptions,
} from "html-react-parser";
import { attributesToProps, domToReact } from "html-react-parser";
import FullscreenIcon from "@svg/fullscreen";
import classNames from "classnames";
import dynamic from "next/dynamic";

import React from "react";
import FormElements from "@modules/form-elements";
import { FormElementAttributes, FormElementsObject } from "@types";
import FormGroup from "@modules/form-elements/form-group";
import AccardionItem from "@modules/form-elements/accardion-item";
import { toFarsiNumberStrict } from "./digits";
import { urlPattern } from "./regex";
import { IFRAME_URLS } from "./constants";
import {
  getMathTextContent,
  parseMathTexContent,
} from "@modules/katex/katexFormatter";
import KatexRenderer from "@modules/katex/KatexRenderer";
import { CODE_LINE_TAGS } from "./helpers";

const MathJax = dynamic(() => import("@modules/course/mathjax/MathJax"), {
  ssr: false,
});

const CodeHighlighter = dynamic(
  () => import("@modules/course/CodeHighlighter"),
  { ssr: false }
);

const isTag = (node: DOMNode | undefined, name: string) =>
  node?.type === "tag" && (node as Element).name === name;

const codeDomToText = (nodes: DOMNode[] = []): string => {
  let text = "";
  let skipSourceNewline = false;

  const consumeBreakWhitespace = (value: string, next?: DOMNode) => {
    if (skipSourceNewline) {
      value = value.replace(/^\r?\n/, "");
      skipSourceNewline = false;
    }
    if (isTag(next, "br")) {
      value = value.replace(/\r?\n$/, "");
    }
    return value;
  };

  for (let index = 0; index < nodes.length; index++) {
    const node = nodes[index];
    const next = nodes[index + 1];

    if (node.type === "text" && "data" in node) {
      text += consumeBreakWhitespace(String(node.data), next);
      continue;
    }

    if (node.type !== "tag") continue;

    const element = node as Element;
    if (element.name === "br") {
      text += "\n";
      skipSourceNewline = true;
      continue;
    }

    let value = element.children?.length
      ? codeDomToText(element.children as DOMNode[])
      : "";
    value = consumeBreakWhitespace(value, next);

    if (CODE_LINE_TAGS.includes(element.name as string)) {
      const followedByLine = nodes.slice(index + 1).some((sibling) => {
        if (sibling.type === "tag") return true;
        return (
          sibling.type === "text" &&
          "data" in sibling &&
          String(sibling.data).replace(/\r?\n/g, "").trim().length > 0
        );
      });
      if (followedByLine && !value.endsWith("\n")) value += "\n";
      if (followedByLine) skipSourceNewline = true;
    }

    text += value;
  }

  return text;
};

const scriptLoadHandler = (node: Element) => {
  const isExternalScript = Boolean(node.attribs?.src);

  const script = document.createElement("script");
  if (isExternalScript) script.src = node.attribs.src;
  else {
    const theChildren = (node?.children || []) as DOMNode[];
    script.innerHTML = (theChildren[0] as any).data;
  }

  return script;
};

export function formHtmlParserOptions(
  elements: FormElementsObject
): HTMLReactParserOptions {
  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      const node = domNode as Element;
      const theChildren = (node?.children || []) as DOMNode[];

      if (node.attribs) {
        node.attribs.style = node.attribs.style?.replace("!important", "");

        if (node.name === "a") {
          const props = attributesToProps(node.attribs);
          return (
            <a {...props} rel="noopener noreferrer">
              {domToReact(theChildren, options)}
            </a>
          );
        } else if (node.name === "iframe") {
          const props = attributesToProps(node.attribs);
          if (String(props.src)?.startsWith("https://docs.google.com/"))
            return <iframe {...props} />;
          return <></>;
        } else if (node.type === "script") {
          const script = scriptLoadHandler(node);
          if (script) document.head.append(script);
        } else if (node.name === "img") {
          const { className, src, ...props } = attributesToProps(node.attribs);

          if (String(className)?.includes("ajax-loader"))
            return (
              <img
                {...props}
                className={classNames("overflow-hidden", className)}
                src={
                  "https://cdn.faradars.org/wp-content/uploads/2023/01/04/ajax-loader.gif"
                }
                alt={typeof props.alt === "string" ? props.alt : "faradars"}
              />
            );
          return (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              {...props}
              className={classNames("overflow-hidden", className)}
              src={String(src).replace(
                "http://faradars.org",
                "https://cdn.faradars.org"
              )}
              alt={typeof props.alt === "string" ? props.alt : "faradars"}
            />
          );
        } else if (node.name === "table") {
          const { border, ...props } = attributesToProps(node.attribs);
          if (border) {
            props.style = props.style
              ? { ...props.style, borderWidth: border + "px" }
              : { borderWidth: border + "px" };
            return <table {...props}>{domToReact(theChildren, options)}</table>;
          }
        } else if (node.name === "group") {
          const { name, ...props } = attributesToProps(node.attribs);
          return (
            <FormGroup
              name={typeof name === "string" ? name : String(name)}
              {...props}
            >
              {domToReact(theChildren, options)}
            </FormGroup>
          );
        } else if (node.name === "accardion") {
          const props = attributesToProps(node.attribs);
          return <div {...props}>{domToReact(theChildren, options)}</div>;
        } else if (node.name === "accardionitem") {
          const props = attributesToProps(node.attribs);
          return (
            <AccardionItem {...props}>
              {domToReact(theChildren, options)}
            </AccardionItem>
          );
        }
      } else if (String(node.type) === "text") {
        const res = Array.from(
          ((node as any).data as string).matchAll(/\[(.*?)\]/g),
          (x) => x[1]
        );
        if (!!res?.length) {
          const parts = ((node as any).data as string).split(/\[(.*?)\]/g);
          // console.log({ res, parts });
          return (
            <>
              {parts.map((p, idx) => {
                const rIdx = res.findIndex((f) => f === p);
                if (rIdx > -1) {
                  const values = Array.from(
                    res[rIdx].matchAll(/"(.*?)"/g),
                    (x) => x[1]
                  );
                  const r = res[rIdx].replaceAll(/"(.*?)"/g, "");
                  const items = r.split(/\s+/);

                  if (items.length > 0) {
                    const [tagWithStar, ...props] = items;
                    const tag = tagWithStar.replace("*", "");
                    const isRequired = tagWithStar.endsWith("*");
                    const _attrs = props.reduce<
                      FormElementAttributes & { prev_continue: boolean }
                    >(
                      (prev, cur, index) => {
                        const sp = cur.split(":");
                        if (prev.prev_continue) {
                          prev.prev_continue = false;
                          return prev;
                        }
                        if (sp.length === 2 && !!sp[1]) {
                          if (sp[0] === "class") {
                            prev.classes.push(sp[1]);
                          } else prev.commonAttrs[sp[0]] = sp[1];
                          return prev;
                        } else if (
                          cur.endsWith(":") &&
                          index + 1 < props.length
                        ) {
                          if (sp[0] === "class") {
                            prev.classes.push(props[index + 1]);
                          } else prev.commonAttrs[sp[0]] = props[index + 1];
                          prev.prev_continue = true;
                          return prev;
                        }
                        if (!prev.commonAttrs["name"])
                          prev.commonAttrs["name"] = cur;
                        else {
                          prev.specificAttrs.push(cur);
                        }
                        return prev;
                      },
                      {
                        specificAttrs: [],
                        commonAttrs: {
                          initialValue: "",
                          name: "",
                        },
                        classes: [],
                        tag,
                        isRequired,
                        values,
                        prev_continue: false,
                      }
                    );
                    const attrs = _attrs;

                    const name = attrs.commonAttrs["name"];
                    if (attrs.specificAttrs.includes("include_blank")) {
                      attrs.values = ["", ...attrs.values];
                    }
                    if (attrs.tag !== "radio" && !!attrs?.values.length) {
                      const hasPlaceholder =
                        attrs.specificAttrs.findIndex(
                          (f) => f === "placeholder" || f === "placeholder="
                        ) > -1;
                      if (!hasPlaceholder) {
                        if (attrs.tag === "checkbox")
                          attrs.commonAttrs["initialValue"] = [];
                        else if (attrs.tag === "select") {
                          attrs.commonAttrs["initialValue"] =
                            !attrs.specificAttrs.includes("multiple")
                              ? attrs.values[0] || ""
                              : [attrs.values[0] || ""];
                        } else if (attrs.tag === "file")
                          attrs.commonAttrs["initialValue"] = null;
                        else
                          attrs.commonAttrs["initialValue"] =
                            attrs.values[0] || "";
                      } else if (hasPlaceholder) {
                        attrs.commonAttrs["placeholder"] = attrs.values[0];
                        attrs.values.splice(0, 1);
                      }
                    }

                    elements[name] = attrs;

                    return (
                      <FormElements key={"form-element" + idx} attrs={attrs} />
                    );
                  }
                }
                return p;
              })}
            </>
          );
        } else {
          return <>{toFarsiNumberStrict((node as any).data as string)}</>;
        }
      }
      return node;
    },
  };
  return options;
}

export function commonHtmlParserOptions(): HTMLReactParserOptions {
  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      const node = domNode as Element;
      const theChildren = (node?.children || []) as DOMNode[];

      if (node.type === "tag" && typeof document !== "undefined") {
        try {
          if (!document.createElement(node.tagName)) {
            const props = attributesToProps(node.attribs);
            return <span {...props}>{node.tagName}</span>;
          }
        } catch (e) {
          const props = attributesToProps(node.attribs);
          return <span {...props}>{node.tagName}</span>;
        }
      }
      if (node.attribs) {
        if (node.name === "a") {
          const props = attributesToProps(node.attribs);
          return (
            <a {...props} rel="noopener noreferrer" target="_blank">
              {domToReact(theChildren, options)}
            </a>
          );
        } else if (node.name === "iframe") {
          const props = attributesToProps(node.attribs);
          return <iframe {...props} className="bg-black" />;
        } else if (node.name === "img") {
          const { width, height, ...props } = attributesToProps(node.attribs);

          const w = parseInt(String(width));
          const h = parseInt(String(height));
          if (w && h)
            return (
              <span
                className={
                  props.style?.marginLeft === "auto" &&
                  props.style?.marginRight === "auto"
                    ? "relative flex w-full justify-center"
                    : props.style?.float === "left"
                      ? "relative float-left"
                      : props.style?.float === "right"
                        ? "relative float-right"
                        : undefined
                }
              >
                <span className="relative z-10">
                  <img
                    src={typeof props.src === "string" ? props.src : ""}
                    alt={typeof props.alt === "string" ? props.alt : "faradars"}
                    width={w}
                    height={h}
                    {...props}
                  />
                </span>
                <div className="absolute bottom-2.5 right-2.5 z-20">
                  <FullscreenIcon />
                </div>
              </span>
            );
          else {
            return (
              <img
                src={typeof props.src === "string" ? props.src : ""}
                alt={typeof props.alt === "string" ? props.alt : "faradars"}
                {...props}
              />
            );
          }
        } else if (node.name === "video") {
          const props = attributesToProps(node.attribs);
          return <video {...props}>{domToReact(theChildren, options)}</video>;
        } else if (node.name === "math") {
          const mathContent = getMathTextContent(theChildren);

          const hasClassName =
            node?.attribs?.class && typeof node.attribs.class === "string";

          const isDisplay = Boolean(
            hasClassName && node.attribs.class?.includes("display-math")
          );
          const useFarsiDigits = Boolean(
            hasClassName && node.attribs.class?.includes("farsi-digits")
          );

          return (
            <KatexRenderer
              math={mathContent}
              displayMode={isDisplay}
              farsiDigits={useFarsiDigits}
            />
          );
        } else if (node.type === "script") {
          let externalScript = node.attribs.src ? true : false;

          // to not duplicate tailwind script
          // if (externalScript && node.attribs.src.includes("tailwindcss"))
          //   return;

          const script = document.createElement("script");
          if (externalScript) {
            script.src = node.attribs.src;
          } else {
            script.innerHTML = (theChildren[0] as any).data;
          }
          document.head.append(script);
        } else if (node.name === "code") {
          const props = attributesToProps(node.attribs);
          return (
            <CodeHighlighter {...props}>
              {codeDomToText(theChildren)}
            </CodeHighlighter>
          );
        }
      }
    },
  };
  return options;
}

export function textLessonHtmlParserOptions(): HTMLReactParserOptions {
  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      const node = domNode as Element;
      const theChildren = (node?.children || []) as DOMNode[];

      if (node.attribs) {
        const props = attributesToProps(node.attribs);
        const { dir = "rtl" } = props;
        props.style = {
          ...props.style,
          direction: String(dir).toLowerCase(),
          textAlign:
            props.style?.textAlign ||
            (String(dir).toLowerCase() === "ltr" ? "left" : ""),
        };
        if (node.name === "a") {
          return (
            <a {...props} rel="noopener noreferrer" target="_blank">
              {domToReact(theChildren, options)}
            </a>
          );
        } else if (node.name === "iframe") {
          const props = attributesToProps(node.attribs);
          if (IFRAME_URLS.some((url) => (props.src as string)?.startsWith(url)))
            return (
              <iframe
                {...props}
                width={props.width ?? props.style?.width ?? "100%"}
                height={props.height ?? props.style?.height ?? 600}
              />
            );
          return <></>;
        } else if (node.name === "audio") {
          return <audio {...props}>{domToReact(theChildren, options)}</audio>;
        } else if (node.name === "source") {
          return <source {...props} />;
        } else if (node.name === "img") {
          const { className, ...rest } = props;
          return (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              {...rest}
              className={classNames(
                "inline-block overflow-hidden rounded-10",
                className
              )}
              alt={typeof props.alt === "string" ? props.alt : "faradars"}
            />
          );
        } else if (node.name === "pre") {
          return (
            <pre className="hljs" {...props}>
              {domToReact(theChildren, options)}
            </pre>
          );
        } else if (
          String(props.className || node.attribs?.class || "").includes(
            "math-tex"
          )
        ) {
          const rawContent = getMathTextContent(theChildren);
          const { math, displayMode } = parseMathTexContent(rawContent);
          const useFarsiDigits = node.attribs?.farsidigits === "true";

          return (
            <KatexRenderer
              math={math}
              displayMode={displayMode}
              farsiDigits={useFarsiDigits}
            />
          );
        } else if (node.name === "math") {
          const mathContent = getMathTextContent(theChildren);

          const hasClassName =
            node?.attribs?.class && typeof node.attribs.class === "string";

          const isDisplay = Boolean(
            hasClassName && node.attribs.class?.includes("display-math")
          );
          const useFarsiDigits = Boolean(
            hasClassName && node.attribs.class?.includes("farsi-digits")
          );

          return (
            <KatexRenderer
              math={mathContent}
              displayMode={isDisplay}
              farsiDigits={useFarsiDigits}
            />
          );
        } else if (node.name === "code") {
          return (
            <CodeHighlighter {...props}>
              {codeDomToText(theChildren)}
            </CodeHighlighter>
          );
        } else {
          if (String(props.className)?.includes("math-tex")) {
            const children = domToReact(theChildren, options);

            return React.createElement(
              node.name,
              props,

              <MathJax dynamic inline>
                <span style={{ direction: "ltr" }}>{children}</span>
              </MathJax>
            );
          } else if (node.name === "colgroup") {
            const cols =
              node.children?.filter(
                (child: any) => child.type === "tag" && child.name === "col"
              ) || [];

            return (
              <colgroup {...props}>
                {cols.map((child: any, i: number) => {
                  const colProps = attributesToProps(child.attribs || {});
                  return <col key={i} {...colProps} />;
                })}
              </colgroup>
            );
          } else if (node.name === "col") {
            return <col {...props} />;
          }
          if (node.name === "iostream") {
            return (
              <code {...props}>
                {`<iostream>${domToReact(theChildren, options)}`}
              </code>
            );
          }
          if (node.name === "hr") {
            return <hr {...props} />;
          }
          if (node.name === "br" || node.name === "input") {
            return React.createElement(node.name, props);
          }
          return React.createElement(
            node.name,
            props,
            domToReact(theChildren, options)
          );
        }
      }
    },
  };
  return options;
}

export function quillHtmlParserOptions(): HTMLReactParserOptions {
  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      const node = domNode as Element;
      const theChildren = (node?.children || []) as DOMNode[];

      if (node.attribs) {
        const { className: cn = "", ...props } = attributesToProps(
          node.attribs
        );
        const children = domToReact(theChildren, options);
        const hasTextChild =
          !!theChildren.length && theChildren[0].type === "text";
        let className = cn;
        let needRenderTextEl = false;
        if (hasTextChild) {
          const hasDirection =
            String(cn).includes("ql-direction-ltr") ||
            String(cn).includes("ql-direction-rtl");
          if (!hasDirection) {
            const txt = children as string;
            if (txt.length > 0) {
              const startsWithEnglish =
                /^[a-z]$/i.test(txt[0]) ||
                /[-!$%^&*()_+|~=`{}\[\]:";'<>?,.\/]/g.test(txt[0]);
              if (startsWithEnglish) {
                className = classNames(
                  className,
                  "ql-direction-ltr ql-align-left"
                );
                needRenderTextEl = true;
              }
            }
          }
        }
        if (
          typeof children === "string" &&
          new RegExp(urlPattern).test(children as string)
        ) {
          return (
            <a
              {...props}
              rel="noopener noreferrer"
              target="_blank"
              className={classNames(className, "cursor-pointer")}
            >
              {children}
            </a>
          );
        }
        if (node.name === "a") {
          return (
            <a
              {...props}
              rel="noopener noreferrer"
              target="_blank"
              className={className as string}
            >
              {children}
            </a>
          );
        } else if (node.name === "math") {
          const mathContent = getMathTextContent(theChildren);

          const hasClassName =
            node?.attribs?.class && typeof node.attribs.class === "string";

          const isDisplay = Boolean(
            hasClassName && node.attribs.class?.includes("display-math")
          );
          const useFarsiDigits = Boolean(
            hasClassName && node.attribs.class?.includes("farsi-digits")
          );

          return (
            <KatexRenderer
              math={mathContent}
              displayMode={isDisplay}
              farsiDigits={useFarsiDigits}
            />
          );
        } else if (node.name === "iframe") {
          return <></>;
        } else if (node.name === "img") {
          return (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              {...props}
              className={classNames(
                "inline-block overflow-hidden rounded-[10px]",
                className
              )}
              alt={typeof props.alt === "string" ? props.alt : "faradars"}
            />
          );
        } else if (node.name === "pre") {
          return (
            <pre className="hljs" {...props}>
              <CodeHighlighter className={cn as string}>
                {codeDomToText(theChildren)}
              </CodeHighlighter>
            </pre>
          );
        } else {
          if (String(className).includes("math-tex")) {
            return React.createElement(
              node.name,
              { ...props, className: cn },
              // <MathComponent></MathComponent>
              <MathJax dynamic>
                <span style={{ direction: "ltr" }}>{children}</span>
              </MathJax>
            );
          } else if (needRenderTextEl) {
            return React.createElement(
              node.name,
              { ...props, className },
              children
            );
          }
        }
      }
    },
  };
  return options;
}
